/**
 * "Pin a point": the point-and-click way to write formula coordinates.
 *
 * A point can follow the object's size along each axis in one of four ways,
 * each written as a readable formula of the live `width` / `depth`:
 *  - stretch — proportional:              `0.35 * width`
 *  - start   — a distance from the left (back) edge:  `-width / 2 + 0.1`
 *  - end     — a distance from the right (front) edge: `width / 2 - 0.1`
 *  - center  — a distance from the middle:              `0.1`
 * The distance may be a parameter (`width / 2 - doorWidth`) — that is how a
 * dimension becomes something users can set on placed objects.
 *
 * Formulas give AUTHORED coordinates that the group's resize rule then maps,
 * so pinning first rewrites the shape's whole group to the identity rule
 * (`fixed`) with every coordinate turned into the formula of its old rule —
 * nothing moves or behaves differently until a point is actually re-pinned.
 */

import type { ObjectAxisResize, ObjectDefinitionSpec, ObjectGroup, ObjectShape, ObjectUniformScale } from 'shared-types';
import { resolveAuthoredShapes, shapesBounds } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { newGroupId, roundCoord } from './specOps';

type Spec = ObjectDefinitionSpec;
export type PinAxis = 'u' | 'n';
export type PinMode = 'stretch' | 'start' | 'end' | 'center';

/** How one axis of a point follows the size; `custom` = a formula the pin modes can't describe. */
export type PinState = { mode: PinMode; distance: number | string } | { mode: 'custom'; formula: string };

const size = (axis: PinAxis): string => (axis === 'u' ? 'width' : 'depth');
const num = (v: number): string => String(Number(v.toFixed(6)));
const extentOf = (spec: Spec, axis: PinAxis): number => (axis === 'u' ? spec.defaultSize.width : spec.defaultSize.depth);

/** The formula of a pin (distance: meters or a param key). */
export function pinFormula(axis: PinAxis, mode: PinMode, distance: number | string): string {
  const d = typeof distance === 'number' ? num(distance) : distance;
  switch (mode) {
    case 'stretch':
      return `${d} * ${size(axis)}`;
    case 'start':
      return `-${size(axis)} / 2 + ${d}`;
    case 'end':
      return `${size(axis)} / 2 - ${d}`;
    case 'center':
      return d;
  }
}

/** Read a formula written by {@link pinFormula} back (anything else is `custom`). */
export function parsePin(axis: PinAxis, formula: string): PinState {
  const s = size(axis);
  const operand = '(-?\\d+(?:\\.\\d+)?|[A-Za-z][A-Za-z0-9]*)';
  const value = (v: string): number | string => (/^-?\d/.test(v) ? Number(v) : v);
  const f = formula.trim();
  let m = new RegExp(`^${operand} \\* ${s}$`).exec(f);
  if (m) return { mode: 'stretch', distance: value(m[1]) };
  m = new RegExp(`^-${s} / 2 \\+ ${operand}$`).exec(f);
  if (m) return { mode: 'start', distance: value(m[1]) };
  m = new RegExp(`^${s} / 2 - ${operand}$`).exec(f);
  if (m) return { mode: 'end', distance: value(m[1]) };
  m = new RegExp(`^${operand}$`).exec(f);
  if (m) return { mode: 'center', distance: value(m[1]) };
  return { mode: 'custom', formula: f };
}

/** The formula fields behind a shape's node (see geometry.shapeNodes); null = not pinnable. */
export function nodeFields(shape: ObjectShape, index: number): { u: string; n: string } | null {
  switch (shape.kind) {
    case 'polyline':
      return index >= 0 && index < shape.points.length ? { u: `p${index}.u`, n: `p${index}.n` } : null;
    case 'rect':
      return [
        { u: 'min.u', n: 'min.n' },
        { u: 'max.u', n: 'min.n' },
        { u: 'max.u', n: 'max.n' },
        { u: 'min.u', n: 'max.n' },
      ][index] ?? null;
    case 'circle':
    case 'arc':
      return index === 0 ? { u: 'center.u', n: 'center.n' } : null;
    case 'path':
      return null;
  }
}

/** A coordinate field's authored value (formulas resolved at the default size). */
function fieldValue(shape: ObjectShape, field: string): number {
  const [head, axis] = field.split('.');
  const pick = (p: readonly [number, number]): number => (axis === 'n' ? p[1] : p[0]);
  switch (shape.kind) {
    case 'polyline':
      return pick(shape.points[Number(head.slice(1))] ?? [0, 0]);
    case 'rect':
      return pick(head === 'min' ? shape.min : shape.max);
    case 'circle':
    case 'arc':
      return pick(shape.center);
    case 'path':
      return 0;
  }
}

/** The live factor of a proportional group as a formula (see ObjectUniformScale). */
function uniformFormula(spec: Spec, rule: ObjectUniformScale): string {
  const su = `width / ${num(spec.defaultSize.width)}`;
  const sn = `depth / ${num(spec.defaultSize.depth)}`;
  return rule === 'width' ? su : rule === 'depth' ? sn : `${rule === 'fit' ? 'min' : 'max'}(${su}, ${sn})`;
}

/** Formula reproducing `rule` for an authored coordinate (an existing formula is wrapped). */
function ruleFormula(rule: ObjectAxisResize, axis: PinAxis, e0: number, groupCenter: number, value: number, formula: string | undefined): string {
  const x = formula ? `(${formula})` : num(value);
  const s = size(axis);
  switch (rule) {
    case 'fixed':
      return formula ?? num(value);
    case 'stretch':
      return formula ? `${x} * ${s} / ${num(e0)}` : pinFormula(axis, 'stretch', value / e0);
    case 'pinStart':
      return formula ? `${x} + ${num(e0 / 2)} - ${s} / 2` : pinFormula(axis, 'start', value + e0 / 2);
    case 'pinEnd':
      return formula ? `${x} - ${num(e0 / 2)} + ${s} / 2` : pinFormula(axis, 'end', e0 / 2 - value);
    case 'float':
      return `${x} + ${num(groupCenter)} * (${s} / ${num(e0)} - 1)`;
  }
}

/** Why a shape can't be pinned (null = it can). */
export function pinBlocker(spec: Spec, shapeId: string): 'curve' | 'repeat' | 'round' | null {
  const shape = spec.shapes.find((s) => s.id === shapeId);
  if (!shape || shape.kind === 'path') return 'curve';
  const group = spec.groups.find((g) => g.shapeIds.includes(shapeId));
  if (group?.repeat && group.repeat.kind !== 'index') return 'repeat';
  // A stretched circle is an ellipse — no radius formula can say that (ungrouped shapes stretch).
  const resize = group?.resize ?? { u: 'stretch', n: 'stretch' };
  const shapes = group ? spec.shapes.filter((s) => group.shapeIds.includes(s.id)) : [shape];
  // A proportional group scales radii too, which pinned centers alone can't say either.
  if (shapes.some((s) => s.kind === 'circle' || s.kind === 'arc') && (resize.uniform || resize.u === 'stretch' || resize.n === 'stretch')) return 'round';
  return null;
}

/**
 * Make the shape's group formula-driven (identity resize), rewriting every
 * coordinate as the formula of the rule it had. An ungrouped shape gets a
 * group of its own. Returns the spec unchanged when already formula-driven.
 */
export function makeFormulaDriven(spec: Spec, shapeId: string): Spec {
  const existing = spec.groups.find((g) => g.shapeIds.includes(shapeId));
  if (existing && existing.resize.u === 'fixed' && existing.resize.n === 'fixed') return spec;
  const group: ObjectGroup = existing ?? { id: newGroupId(), name: spec.shapes.find((s) => s.id === shapeId)?.name ?? 'Pinned', shapeIds: [shapeId], resize: { u: 'stretch', n: 'stretch' } };
  const authored = new Map(resolveAuthoredShapes(spec).map((s) => [s.id, s]));
  const members = spec.shapes.filter((s) => group.shapeIds.includes(s.id));
  const authoredMembers = members.map((s) => authored.get(s.id) ?? s);
  const center = { u: (shapesBounds(authoredMembers, 0).min + shapesBounds(authoredMembers, 0).max) / 2, n: (shapesBounds(authoredMembers, 1).min + shapesBounds(authoredMembers, 1).max) / 2 };

  const rewrite = (shape: ObjectShape): ObjectShape => {
    const resolved = authored.get(shape.id) ?? shape;
    const fields: string[] = [];
    if (shape.kind === 'polyline') shape.points.forEach((_, i) => fields.push(`p${i}.u`, `p${i}.n`));
    else if (shape.kind === 'rect') fields.push('min.u', 'min.n', 'max.u', 'max.n');
    else if (shape.kind === 'circle' || shape.kind === 'arc') fields.push('center.u', 'center.n');
    const expr: Record<string, string> = { ...shape.expr };
    for (const field of fields) {
      const axis: PinAxis = field.endsWith('.n') ? 'n' : 'u';
      const uniform = group.resize.uniform;
      const value = fieldValue(resolved, field);
      const formula = shape.expr?.[field];
      expr[field] = uniform ? `${formula ? `(${formula})` : num(value)} * ${uniformFormula(spec, uniform)}` : ruleFormula(group.resize[axis], axis, extentOf(spec, axis), center[axis], value, formula);
    }
    return { ...shape, expr };
  };

  const nextGroup: ObjectGroup = { ...group, resize: { u: 'fixed', n: 'fixed' } };
  return {
    ...spec,
    shapes: spec.shapes.map((s) => (group.shapeIds.includes(s.id) ? rewrite(s) : s)),
    groups: existing ? spec.groups.map((g) => (g.id === existing.id ? nextGroup : g)) : [...spec.groups, nextGroup],
  };
}

/** How a node follows the size along `axis` now (from its formula, or from its group's rule). */
export function pinStateOf(spec: Spec, shapeId: string, index: number, axis: PinAxis): PinState | null {
  const shape = spec.shapes.find((s) => s.id === shapeId);
  const fields = shape ? nodeFields(shape, index) : null;
  if (!shape || !fields) return null;
  const group = spec.groups.find((g) => g.shapeIds.includes(shapeId));
  if (group?.resize.uniform) return { mode: 'custom', formula: 'uniform' };
  const rule = group?.resize[axis] ?? 'stretch';
  const formula = shape.expr?.[fields[axis]];
  if (formula !== undefined) {
    // Formulas are literal only under the identity rule.
    if (rule === 'fixed') return parsePin(axis, formula);
    return { mode: 'custom', formula };
  }
  const resolved = resolveAuthoredShapes(spec).find((s) => s.id === shapeId) ?? shape;
  const value = fieldValue(resolved, fields[axis]);
  const e0 = extentOf(spec, axis);
  switch (rule) {
    case 'stretch':
      return { mode: 'stretch', distance: Number((value / e0).toFixed(6)) };
    case 'pinStart':
      return { mode: 'start', distance: roundCoord(value + e0 / 2) };
    case 'pinEnd':
      return { mode: 'end', distance: roundCoord(e0 / 2 - value) };
    case 'fixed':
      return { mode: 'center', distance: roundCoord(value) };
    case 'float':
      return { mode: 'custom', formula: 'float' };
  }
}

/**
 * Pin a node along `axis`. `distance` may be a param key; without one the
 * point keeps its current place (its distance is measured from the new
 * reference).
 */
export function pinNode(spec: Spec, shapeId: string, index: number, axis: PinAxis, mode: PinMode, distance?: number | string): Spec {
  if (pinBlocker(spec, shapeId)) return spec;
  const driven = makeFormulaDriven(spec, shapeId);
  const shape = driven.shapes.find((s) => s.id === shapeId);
  const fields = shape ? nodeFields(shape, index) : null;
  if (!shape || !fields) return spec;
  const e0 = extentOf(driven, axis);
  const current = fieldValue(resolveAuthoredShapes(driven).find((s) => s.id === shapeId) ?? shape, fields[axis]);
  const measured = mode === 'stretch' ? current / e0 : mode === 'start' ? current + e0 / 2 : mode === 'end' ? e0 / 2 - current : current;
  const d = distance ?? (mode === 'stretch' ? Number(measured.toFixed(6)) : roundCoord(measured));
  const formula = pinFormula(axis, mode, d);
  return { ...driven, shapes: driven.shapes.map((s) => (s.id === shapeId ? { ...s, expr: { ...s.expr, [fields[axis]]: formula } } : s)) };
}

/** Whether an axis of a node can be dragged: plain coordinates, or a pin with a typed distance. */
function axisDraggable(spec: Spec, shape: ObjectShape, index: number, axis: PinAxis): boolean {
  const fields = nodeFields(shape, index);
  if (!fields || shape.expr?.[fields[axis]] === undefined) return true;
  const state = pinStateOf(spec, shape.id, index, axis);
  return state !== null && state.mode !== 'custom' && typeof state.distance === 'number';
}

/** A node can be dragged when at least one axis can (the other keeps following its formula). */
export function nodeDraggable(spec: Spec, shapeId: string, index: number): boolean {
  const shape = spec.shapes.find((s) => s.id === shapeId);
  if (!shape) return false;
  if (!shape.expr) return true;
  return axisDraggable(spec, shape, index, 'u') || axisDraggable(spec, shape, index, 'n');
}

/**
 * Drag a node of a formula-driven shape to `p` (authored, default size):
 * each pinned axis keeps its reference edge and takes the new distance;
 * axes on a parameter or a custom formula stay where their formula puts them.
 */
export function dragPinnedNode(spec: Spec, shapeId: string, index: number, p: readonly [number, number]): Spec {
  const shape = spec.shapes.find((s) => s.id === shapeId);
  const fields = shape ? nodeFields(shape, index) : null;
  if (!shape || !fields) return spec;
  const expr: Record<string, string> = { ...shape.expr };
  (['u', 'n'] as const).forEach((axis, i) => {
    if (!axisDraggable(spec, shape, index, axis)) return;
    const state = pinStateOf(spec, shapeId, index, axis);
    if (!state || state.mode === 'custom' || shape.expr?.[fields[axis]] === undefined) return;
    const e0 = extentOf(spec, axis);
    const v = p[i];
    const d = state.mode === 'stretch' ? Number((v / e0).toFixed(6)) : state.mode === 'start' ? roundCoord(v + e0 / 2) : state.mode === 'end' ? roundCoord(e0 / 2 - v) : roundCoord(v);
    expr[fields[axis]] = pinFormula(axis, state.mode, d);
  });
  return { ...spec, shapes: spec.shapes.map((s) => (s.id === shapeId ? { ...s, expr } : s)) };
}
